import React, { useState, useRef, useEffect, createContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CloseIcon, ArrowBackIcon, MenuIcon } from '../AdminIcon';

// Routed pages publish chrome here while the shell keeps the actual header mounted.
export const SliderHeaderContext = createContext(null);

export default function SliderHeader({ name, index = 0, onClick, canGoBack = true }) {
   const [menuOpen, setMenuOpen] = useState(false);
   const { pathname } = useLocation();
   const menuRef = useRef(null);
   const headingRef = useRef(null);
   useEffect(() => {
      setMenuOpen(false);
      headingRef.current?.focus({ preventScroll: true });
   }, [name, pathname]);
   const history = useHistory();

   const isNavClickable = canGoBack && typeof onClick === 'function';

   // Close menu when clicking outside
   useEffect(() => {
      if (!menuOpen) return;
      const handleClickOutside = (e) => {
         if (menuRef.current && !menuRef.current.contains(e.target)) {
            setMenuOpen(false);
         }
      };
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      return () => {
         document.removeEventListener('mousedown', handleClickOutside);
         document.removeEventListener('touchstart', handleClickOutside);
      };
   }, [menuOpen]);

   const handleCloseKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
         e.preventDefault();
         e.stopPropagation();
         onClick();
      }
   };

   const handleExitKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
         e.preventDefault();
         e.stopPropagation();
         history.push('/');
      }
   };

   return (
            <nav
               onClick={isNavClickable ? onClick : null}
               aria-label={name}
            >
               {/* Close Icon on the Left */}
               {isNavClickable && (
                  <button
                     className="close-icon"
                     onClick={(e) => {
                        e.stopPropagation();
                        onClick();
                     }}
                     onKeyDown={handleCloseKeyDown}
                     aria-label="Close Slider"
                     type="button"
                  >
                     <ArrowBackIcon aria-hidden="true" />
                  </button>
               )}

               {/* Slider Name Centered */}
               <h1 ref={headingRef} tabIndex={-1} className="slider-name">{name}</h1>

               {/* Desktop: inline controls */}
               <div
                  className="nav-right-controls nav-right-desktop"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
               >
                  {index === 0 && localStorage.getItem('pinpoint_kiosk_mode') === 'true' && (
                     <button
                        onClick={(e) => {
                           e.stopPropagation();
                           localStorage.removeItem('pinpoint_kiosk_mode');
                           history.push('/kiosk');
                        }}
                        aria-label="Exit to Kiosk"
                        type="button"
                        className="slider-exit-button"
                     >
                        Exit
                     </button>
                  )}

                  {index === 0 && (
                     <button
                        onClick={(e) => {
                           e.stopPropagation();
                           history.push('/');
                        }}
                        onKeyDown={handleExitKeyDown}
                        aria-label="Close admin"
                        title="Close admin"
                        type="button"
                        className="slider-close-button"
                     >
                        <CloseIcon boxSize={4} aria-hidden="true" />
                     </button>
                  )}
               </div>

               {/* Mobile: root navigation actions */}
               {index === 0 && <div
                  className="nav-right-mobile"
                  ref={menuRef}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                     e.stopPropagation();
                     if (e.key === 'Escape') { setMenuOpen(false); menuRef.current?.querySelector('button')?.focus(); }
                  }}
               >
                  <button
                     className="mobile-menu-button"
                     onClick={() => setMenuOpen((prev) => !prev)}
                     aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                     aria-expanded={menuOpen}
                     type="button"
                  >
                     {menuOpen ? <CloseIcon /> : <MenuIcon />}
                  </button>
                  {menuOpen && (
                     <div className="mobile-menu-dropdown">
                        {index === 0 && (
                           <button
                              className="mobile-menu-item mobile-menu-action"
                              onClick={() => {
                                 history.push('/');
                                 setMenuOpen(false);
                              }}
                              type="button"
                           >
                              Close admin
                           </button>
                        )}

                        {index === 0 && localStorage.getItem('pinpoint_kiosk_mode') === 'true' && (
                           <button
                              className="mobile-menu-item mobile-menu-action slider-exit-button"
                              onClick={() => {
                                 localStorage.removeItem('pinpoint_kiosk_mode');
                                 history.push('/kiosk');
                                 setMenuOpen(false);
                              }}
                              type="button"
                           >
                              Exit to Kiosk
                           </button>
                        )}
                     </div>
                  )}
               </div>}
            </nav>
   );
}
